import React, { useState } from 'react'
import { Redirect } from 'react-router-dom'
import { Location, History } from 'history'
import { withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { Navigation } from 'navigation'
import OrdersSectionTabs from 'orders/sectionTabs'
import { Sorting, Pagination } from 'uiComponents/table'
import { CompareRangeQuery } from 'uiComponents/popups/comparisonDateRangePicker/schema'
import { PaypalService } from 'orders/paypalService'
import DisputesPage from './disputesPage'
import { Container, FullWidthContainerBody } from 'uiComponents/settingsContainer'
import { SecondaryTabsRow } from 'uiComponents/navigation/secondaryTabsRow'
import { TableToolbar } from 'uiComponents/table/tableToolbar'
import DateRangePicker from 'uiComponents/popups/comparisonDateRangePicker'
import { Checkbox } from 'uiComponents/input'
import { OrdersService } from 'orders/ordersService'
import PresetDateRangeElement, { PresetType } from 'uiComponents/popups/presetsElement'
import { dateRangeWithName, DateRange, DateRangeName, last30days } from 'dateRanges'
import { useAppSelector } from 'store/hooks'
import { useMessages } from 'messagesContext'
import { useTranslation } from 'translations'
import './styles.scss'

interface PaypalDisputesPageProps {
    history: History
    location: Location
    dateRange: DateRange
    onDateRangeChanged: (dr: DateRange, compareRange: CompareRangeQuery) => void
    sort: Sorting
    onSortChanged: (s: Sorting) => void
    pagination: Pagination
    onPaginationChanged: (p: Pagination) => void
    accountSlug: string
    navigation: Navigation
    match: RouteMatch<{}>
    paypalService: PaypalService
    ordersService: OrdersService
}

function PaypalDisputesPage(props: PaypalDisputesPageProps) {
    const { t } = useTranslation()
    const query = props.navigation.query()
    const [allPartners, setAllPartners] = useState<boolean>(!!query.showAllPartners)
    const user = useAppSelector((state) => state.auth.user)
    const { removeAllMessages, replaceMessages } = useMessages()

    const onAllPartnersChange = () => {
        props.navigation.addQueryWithReplace({
            showAllPartners: allPartners ? null : 'true',
        })
        setAllPartners(!allPartners)
    }

    const presetName = 'activeDisputesDateRange'
    const defaultPreset: PresetType = 'last30days'
    const presetOptions: PresetType[] = ['last30days', 'last90days', 'lifetime']

    const range = query.activeDisputesDateRange || 'last30days'
    const activeDateRange = dateRangeWithName(range as DateRangeName) || last30days()

    const section: 'active' | 'resolved' = query.status === 'resolved' ? 'resolved' : 'active'

    const legacySection = props.location.pathname.match(/\/disputes\/(active|resolved)\/?$/)?.[1]

    if (legacySection) {
        const params = new URLSearchParams(props.location.search)

        if (legacySection === 'resolved') {
            params.set('status', 'resolved')
        } else {
            params.delete('status')
        }

        const search = params.toString()

        return <Redirect to={`/account/${props.accountSlug}/orders/paypal/disputes${search ? `?${search}` : ''}`} />
    }

    return (
        <div style={{ position: 'relative' }}>
            <OrdersSectionTabs />
            <div>
                <SecondaryTabsRow
                    tabs={(['active', 'resolved'] as const).map((value) => ({
                        name: value === 'active' ? 'Active disputes' : 'Resolved disputes',
                        current: section === value,
                        onClick: () =>
                            props.navigation.addQueryWithReplace({ status: value === 'active' ? null : value }),
                    }))}
                />
            </div>
            <Container>
                <TableToolbar>
                    {section === 'resolved' ? (
                        <DateRangePicker range={props.dateRange} onChange={props.onDateRangeChanged} />
                    ) : (
                        <PresetDateRangeElement
                            presetName={presetName}
                            default={defaultPreset}
                            options={presetOptions}
                        />
                    )}
                    {user?.isAdmin && (
                        <label className="all-partners-toggle">
                            <Checkbox checked={!!query.showAllPartners} onChange={onAllPartnersChange} />
                            <span>{t('orders.paypalDisputes.showAllPartners')}</span>
                        </label>
                    )}
                </TableToolbar>
                <FullWidthContainerBody>
                    <DisputesPage
                        section={section}
                        history={props.history}
                        accountSlug={props.accountSlug}
                        sort={props.sort}
                        onSortChanged={props.onSortChanged}
                        pagination={props.pagination}
                        onPaginationChanged={props.onPaginationChanged}
                        dateRange={section === 'resolved' ? props.dateRange : activeDateRange}
                        paypalService={props.paypalService}
                        ordersService={props.ordersService}
                        showAllPartners={!!query.showAllPartners && (user?.isAdmin ?? false)}
                        replaceMessages={replaceMessages}
                        removeAllMessages={removeAllMessages}
                    />
                </FullWidthContainerBody>
            </Container>
        </div>
    )
}

export default withNavigation(PaypalDisputesPage)
